import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Checks, CircleNotch } from "@phosphor-icons/react";

const POLL_MS = 60000; // 1 min — bell doesn't need to be chatty

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function BellDropdown() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const markingAllRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/agent/bell");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silently ignore — bell is non-critical */
    }
  }, []);

  useEffect(() => {
    load();
    const h = setInterval(load, POLL_MS);
    return () => clearInterval(h);
  }, [load]);

  const markAllRead = async () => {
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    setLoading(true);
    try {
      await api.post("/agent/bell/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } finally {
      markingAllRef.current = false;
      setLoading(false);
    }
  };

  const onClickItem = async (n) => {
    try {
      if (!n.read) await api.post(`/agent/bell/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    } catch { /* ignore */ }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative text-white/70 hover:text-[#D4AF37] transition-colors p-2 rounded-md hover:bg-[#D4AF37]/10"
          data-testid="bell-trigger"
          aria-label="Notifications"
        >
          <Bell size={20} weight={unread > 0 ? "fill" : "duotone"} className={unread > 0 ? "text-[#D4AF37]" : ""} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-sans font-semibold flex items-center justify-center"
              data-testid="bell-unread-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[360px] max-w-[92vw] bg-ink-surface border-white/15 text-white p-0 overflow-hidden"
        data-testid="bell-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <p className="font-serif text-sm">Notifications</p>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={loading}
              className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#F3C853] font-sans flex items-center gap-1"
              data-testid="bell-mark-all-read"
            >
              {loading ? <CircleNotch size={12} className="animate-spin" /> : <Checks size={12} weight="duotone" />}
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="bell-list">
          {items.length === 0 && (
            <div className="p-8 text-center text-white/50 text-xs font-sans italic">
              No notifications yet — you'll see updates here.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => onClickItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-b-0 transition-colors ${
                n.read ? "hover:bg-white/5" : "bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10"
              }`}
              data-testid={`bell-item-${n.kind}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" aria-hidden="true" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-sans ${n.read ? "text-white/70" : "text-white"}`}>{n.title}</p>
                  <p className="text-[11.5px] text-white/55 font-sans mt-0.5 leading-relaxed">{n.body}</p>
                  <p className="text-[10px] text-white/35 font-sans mt-1.5">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
